import { useEffect, useState } from 'react';

// Coordinates are expressed in layout-viewport CSS pixels. In Chrome Android the
// keyboard can shrink the visual viewport without resizing the layout viewport.
export function readVisualViewport() {
  if (typeof window === 'undefined') return { height: 0, top: 0, bottomInset: 0 };
  const viewport = window.visualViewport;
  const top = Math.max(0, viewport?.offsetTop || 0);
  const height = Math.max(0, viewport?.height || window.innerHeight);
  const bottomInset = Math.max(0, window.innerHeight - top - height);
  return {
    height: Math.round(height),
    top: Math.round(top),
    bottomInset: Math.round(bottomInset),
  };
}

export default function useVisualViewport(enabled = true) {
  const [bounds, setBounds] = useState(readVisualViewport);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const viewport = window.visualViewport;
    let frame = null;
    const update = () => {
      frame = null;
      const next = readVisualViewport();
      setBounds(previous => (
        previous.height === next.height && previous.top === next.top && previous.bottomInset === next.bottomInset
          ? previous : next
      ));
    };
    const schedule = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };

    update();
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    document.addEventListener('fullscreenchange', schedule);
    document.addEventListener('webkitfullscreenchange', schedule);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      document.removeEventListener('fullscreenchange', schedule);
      document.removeEventListener('webkitfullscreenchange', schedule);
    };
  }, [enabled]);

  return bounds;
}
